import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { RefreshCw, CheckCircle2, AlertCircle, History } from 'lucide-react'
import { toast } from 'sonner'
import { ChonkyButton } from '@/components/shared/chonky-button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api } from '@/lib/api'

interface GuruSyncCardProps {
  merchantId: string
}

export function GuruSyncCard({ merchantId }: GuruSyncCardProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [daysBack, setDaysBack] = useState(1)

  const syncMutation = useMutation({
    mutationFn: async () => {
      return api.settings.syncGuruTransactions({
        merchantId,
        daysBack: Number(daysBack),
      })
    },
    onSuccess: () => {
      setIsOpen(false)
      toast.success('Sincronização agendada', {
        description: `O processo foi iniciado em segundo plano. Os eventos aparecerão nos logs em breve.`,
        icon: <CheckCircle2 className="text-green-500" />,
      })
    },
    onError: () => {
      toast.error('Erro na sincronização', {
        description: 'Não foi possível iniciar o sync. Tente novamente.',
      })
    },
  })

  return (
    <Card className="border-orange-100 bg-orange-50/10 dark:border-orange-900/20">
      <CardHeader>
        <div className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5 text-orange-600" />
          <CardTitle>Recuperação de Eventos (Guru)</CardTitle>
        </div>
        <CardDescription>
          Ferramenta de diagnóstico e recuperação para eventos perdidos.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md bg-white p-4 text-sm text-muted-foreground border dark:bg-zinc-900">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 text-blue-500 shrink-0" />
            <p>
              Use esta ferramenta se você notar que pedidos no Guru não foram processados no
              Rafobase (ex: falhas de webhook). O sistema irá buscar as transações passadas e
              processá-las novamente.
            </p>
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <ChonkyButton variant="secondary" className="w-full sm:w-auto">
              <History className="mr-2 h-4 w-4" />
              Sincronizar Transações Perdidas
            </ChonkyButton>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Sincronizar Transações</DialogTitle>
              <DialogDescription>
                Isso buscará todas as transações no Guru dentro do período selecionado e tentará
                processá-las novamente. Transações já processadas serão ignoradas (idempotência).
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="days">Período de busca (dias atrás)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="days"
                    type="number"
                    min={1}
                    max={30}
                    value={daysBack}
                    onChange={(e) => setDaysBack(Number(e.target.value))}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">Dias (Máximo 30)</span>
                </div>
              </div>
            </div>

            <DialogFooter>
              <ChonkyButton onClick={() => syncMutation.mutate()} loading={syncMutation.isPending}>
                Inciar Sincronização
              </ChonkyButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardFooter>
    </Card>
  )
}
